/**
 * BusinessRegisterPage — Öffentliche Selbstregistrierung für Betriebe/Mandanten.
 * Route: /betrieb-registrieren (keine Auth). Legt sofort einen Mandanten +
 * Admin-Konto an und leitet in den Einrichtungs-Assistenten weiter.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Building2, Loader2, ArrowRight, Check, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BusinessRegisterPage() {
  const [industries, setIndustries] = useState([]);
  const [loadingInd, setLoadingInd] = useState(true);
  const [branche, setBranche] = useState('');
  const [form, setForm] = useState({ company_name: '', admin_email: '', admin_password: '', contact_phone: '', city: '' });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    axios.get(`${API}/public/register-business/industries`)
      .then((r) => setIndustries(r.data.industries || []))
      .catch(() => toast.error('Branchen konnten nicht geladen werden'))
      .finally(() => setLoadingInd(false));
  }, []);

  const submit = async () => {
    if (!branche) { toast.error('Bitte eine Branche wählen'); return; }
    if (form.company_name.trim().length < 2) { toast.error('Bitte einen Firmennamen eingeben'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.admin_email.trim())) { toast.error('Bitte eine gültige E-Mail eingeben'); return; }
    if (form.admin_password.length < 8) { toast.error('Das Passwort muss mindestens 8 Zeichen haben'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/register-business`, { ...form, branche });
      localStorage.setItem('token', data.access_token);
      toast.success('Willkommen! Dein Betrieb wird eingerichtet …');
      setTimeout(() => { window.location.href = data.redirect || '/einrichtung'; }, 600);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Registrierung fehlgeschlagen');
      setSubmitting(false);
    }
  };

  // Branchen nach Gruppe (Produktfamilie) bündeln
  const groups = industries.reduce((acc, it) => {
    const g = it.group || 'Weitere';
    (acc[g] = acc[g] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <Toaster position="top-center" richColors />
      <div className="mx-auto max-w-3xl" data-testid="business-register-page">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white">
            <Building2 className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Betrieb registrieren</h1>
          <p className="mt-2 text-sm text-slate-500">Branche wählen, Firma anlegen – 30 Tage kostenlos testen.</p>
        </div>

        {/* Schritt 1: Branche */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">1</span>
            <h2 className="text-lg font-semibold text-slate-900">Deine Branche</h2>
          </div>
          {loadingInd ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-5">
              {Object.entries(groups).map(([g, items]) => (
                <div key={g}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{g}</p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {items.map((it) => {
                      const active = branche === it.branche;
                      return (
                        <button key={it.branche} type="button" onClick={() => setBranche(it.branche)}
                          data-testid={`bizreg-industry-${it.branche}`}
                          className={`relative rounded-xl border px-3 py-3 text-left text-sm transition ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}>
                          <span className="font-medium">{it.label}</span>
                          {active && <Check className="absolute right-2 top-2 h-4 w-4" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Schritt 2: Firma + Login */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">2</span>
            <h2 className="text-lg font-semibold text-slate-900">Firma & Zugang</h2>
          </div>
          <div className="grid gap-4">
            <div>
              <Label>Firmenname *</Label>
              <Input value={form.company_name} onChange={set('company_name')} placeholder="z. B. Vitalis GmbH" className="mt-1" data-testid="bizreg-company" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Admin-E-Mail *</Label>
                <Input type="email" value={form.admin_email} onChange={set('admin_email')} placeholder="du@firma.ch" className="mt-1" data-testid="bizreg-email" />
              </div>
              <div>
                <Label>Passwort * <span className="text-slate-400">(min. 8 Zeichen)</span></Label>
                <Input type="password" value={form.admin_password} onChange={set('admin_password')} placeholder="••••••••" className="mt-1" data-testid="bizreg-password" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Telefon</Label>
                <Input value={form.contact_phone} onChange={set('contact_phone')} placeholder="+41 44 123 45 67" className="mt-1" data-testid="bizreg-phone" />
              </div>
              <div>
                <Label>Ort</Label>
                <Input value={form.city} onChange={set('city')} placeholder="Zürich" className="mt-1" data-testid="bizreg-city" />
              </div>
            </div>
          </div>
        </div>

        <Button onClick={submit} disabled={submitting} data-testid="bizreg-submit"
          className="w-full bg-slate-900 py-6 text-base font-semibold text-white hover:bg-slate-800">
          {submitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
          Betrieb erstellen & Testmonat starten <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" /> Eigene geschützte Datenbank pro Betrieb · keine Zahlung vor Ablauf des Tests
        </p>
      </div>
    </div>
  );
}
